import axios from "axios";

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Token lives in localStorage when "Remember me" was checked at sign-in
// (persists across browser sessions), or in sessionStorage when not
// (cleared when the tab/browser closes).
const TOKEN_KEY = "kalib_token";

API.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function setToken(token, remember = true) {
  if (token) {
    // Always clear the other bucket first so we don't leave a stale token behind.
    if (remember) {
      sessionStorage.removeItem(TOKEN_KEY);
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.setItem(TOKEN_KEY, token);
    }
  } else {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  }
}

export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
}

/**
 * Build a human-readable, actionable error from an axios/fetch error.
 *
 * Returns either a plain string (for the simple case) or — when callers opt
 * in via the second arg `{ structured: true }` — an object of the form:
 *   { message, kind, cta }
 * where `kind` is one of:
 *   "account_exists"   — backend says email is already registered
 *   "network"          — request never reached the server / CORS blocked
 *   "validation"       — 4xx with a detail string
 *   "server"           — 5xx
 *   "unknown"          — fallback
 * and `cta` is an optional shape { label, to } the page can render as a link.
 */
export function formatApiError(error, opts = {}) {
  const { structured = false } = opts;
  const detail = error?.response?.data?.detail;
  const status = error?.response?.status;

  // ── 1. Network / CORS error: no response object at all ─────────────
  if (!error?.response) {
    const out = {
      message: "Couldn't reach the server. Check your connection and try again.",
      kind: "network",
      cta: null,
    };
    return structured ? out : out.message;
  }

  // ── 2. Email already registered → recognisable across phrasings ────
  const detailStr = typeof detail === "string" ? detail : "";
  const looksLikeAccountExists =
    status === 400 &&
    /already (registered|exists|in use)|email .* exists/i.test(detailStr);
  if (looksLikeAccountExists) {
    const out = {
      message: "You already have an account with this email. Sign in instead.",
      kind: "account_exists",
      cta: { label: "Sign in", to: "/signin" },
    };
    return structured ? out : out.message;
  }

  // ── 3. 5xx → server-side issue ─────────────────────────────────────
  if (status >= 500) {
    const out = {
      message: "Server error. Please try again in a moment.",
      kind: "server",
      cta: null,
    };
    return structured ? out : out.message;
  }

  // ── 4. Standard validation / 4xx with a detail payload ─────────────
  let message;
  if (detail == null) {
    message = "Something went wrong. Please try again.";
  } else if (typeof detail === "string") {
    message = detail;
  } else if (Array.isArray(detail)) {
    message = detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  } else if (detail && typeof detail.msg === "string") {
    message = detail.msg;
  } else {
    message = String(detail);
  }
  const out = { message, kind: "validation", cta: null };
  return structured ? out : out.message;
}

export default API;
